<template>
  <div class="user-list-container">
    <div class="page-header">
      <h2 class="page-title">用户管理</h2>
    </div>

    <!-- 搜索区域 -->
    <div class="search-container">
      <el-form :inline="true" :model="searchForm" class="demo-form-inline">
        <el-form-item label="账号">
          <el-input v-model="searchForm.username" placeholder="请输入账号" clearable></el-input>
        </el-form-item>
        <el-form-item label="名称">
          <el-input v-model="searchForm.realName" placeholder="请输入名称" clearable></el-input>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="正常" :value="1"></el-option>
            <el-option label="禁用" :value="0"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="resetSearchForm">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 用户列表 -->
    <el-table
      v-loading="loading"
      :data="userList"
      border
      style="width: 100%">
      <el-table-column
        prop="id"
        label="ID"
        width="80">
      </el-table-column>
      <el-table-column
        prop="username"
        label="账号"
        width="200">
      </el-table-column>
      <el-table-column
        prop="realName"
        label="名称"
        width="120">
      </el-table-column>
      <el-table-column
        prop="phone"
        label="手机号"
        width="120">
      </el-table-column>
      <el-table-column
        prop="roles"
        label="角色"
        width="280">
        <template slot-scope="scope">
          <el-tag v-for="(role, index) in scope.row.roles" :key="index" type="success" size="small" class="mr-1">
            {{ role }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column
        prop="status"
        label="状态"
        width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
            {{ scope.row.status === 1 ? '正常' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column
        label="操作"
        width="460">
        <template slot-scope="scope">
          <el-button 
            size="mini" 
            :type="scope.row.status === 1 ? 'warning' : 'success'"
            @click="handleStatusChange(scope.row)">
            {{ scope.row.status === 1 ? '禁用' : '启用' }}
          </el-button>
          <el-button size="mini" type="success" @click="handleAssignRoles(scope.row)">分配角色</el-button>
          <el-button size="mini" type="primary" @click="handleAssignBridges(scope.row)">关联桥梁</el-button>
          <el-button size="mini" type="primary" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.pageNum"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total">
      </el-pagination>
    </div>

    <!-- 角色分配对话框 -->
    <el-dialog title="分配角色" :visible.sync="roleDialogVisible" width="500px">
      <el-form :model="roleForm" label-width="80px">
        <el-form-item label="账号">
          <span>{{ currentUser.username }}</span>
        </el-form-item>
        <el-form-item label="角色">
          <div class="role-selection">
            <el-checkbox-group v-model="roleForm.roleIds">
              <el-checkbox 
                v-for="role in allRoles" 
                :key="role.id" 
                :label="Number(role.id)"
                class="role-checkbox">
                {{ role.roleName }}
              </el-checkbox>
            </el-checkbox-group>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="roleDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveUserRoles" :loading="saveRolesLoading">确定</el-button>
      </div>
    </el-dialog>

    <!-- 桥梁关联对话框 -->
    <el-dialog title="关联桥梁" :visible.sync="bridgeDialogVisible" width="800px">
      <el-form :model="bridgeForm" label-width="80px">
        <el-form-item label="账号">
          <span>{{ currentUser.username }}</span>
        </el-form-item>
        <el-form-item label="关联桥梁">
          <bridge-transfer 
            ref="bridgeTransfer"
            v-model="bridgeForm.bridgeIds" 
            :bridge-list="allBridges"
            :loading="saveBridgesLoading"
            :user-id="currentUser.id"
            :auto-load="false"
            @change="handleBridgeChange"
            @loaded="handleBridgesLoaded"
            @load-error="handleBridgeLoadError"
          ></bridge-transfer>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="bridgeDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveUserBridges" :loading="saveBridgesLoading">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getUserList, getAllRoles, getUserRoles, assignUserRoles, updateUserStatus, deleteUser } from '@/api/auth'
import { getAllBridges, assignUserBridges } from '@/api/bridge'
import BridgeTransfer from '@/components/common/BridgeTransfer.vue'

export default {
  name: 'UserList',
  components: {
    BridgeTransfer
  },
  inject: {
    registerNavButtons: {
      default: () => () => {}
    },
    unregisterNavButtons: {
      default: () => () => {}
    }
  },
  data() {
    return {
      loading: false,
      userList: [],
      searchForm: {
        username: '',
        realName: '',
        status: ''
      },
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      // 角色相关
      roleDialogVisible: false,
      currentUser: {},
      allRoles: [],
      roleForm: {
        roleIds: []
      },
      // 桥梁相关
      bridgeDialogVisible: false,
      allBridges: [],
      bridgeForm: {
        bridgeIds: []
      },
      saveRolesLoading: false,
      saveBridgesLoading: false
    }
  },
  computed: {
    // 检查是否有缺失的桥梁
    hasMissingBridges() {
      return this.allBridges.some(bridge => bridge.isMissing);
    }
  },
  created() {
    this.fetchUserList()
    this.fetchAllRoles()
    this.fetchAllBridges()
    
    // 注册导航按钮
    this.registerNavButtons([
      {
        id: 'add-user',
        text: '添加用户',
        icon: 'el-icon-plus',
        type: 'primary',
        handler: () => this.$router.push('/user/management')
      }
    ])
  },
  beforeDestroy() {
    // 在组件销毁前注销按钮
    this.unregisterNavButtons()
  },
  methods: {
    // 加载用户列表
    async fetchUserList() {
      this.loading = true
      try {
        const { pageNum, pageSize } = this.pagination
        const { username, realName, status } = this.searchForm
        
        const params = {
          pageNum: pageNum,
          pageSize: pageSize,
          username: username || '',
          realName: realName || '',
          status: status !== '' ? status : null
        }
        
        const res = await getUserList(params)
        if (res.code === 200) {
          this.userList = res.data.list || res.data.content || []
          this.pagination.total = res.data.total || res.data.totalElements || 0
        } else {
          this.$message.error(res.message || '获取用户列表失败')
        }
      } catch (error) {
        console.error('获取用户列表出错:', error)
        this.$message.error('获取用户列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 获取所有角色
    async fetchAllRoles() {
      try {
        const res = await getAllRoles()
        if (res.code === 200) {
          this.allRoles = res.data
        }
      } catch (error) {
        console.error('获取角色列表出错:', error)
      }
    },
    
    // 获取所有桥梁
    async fetchAllBridges() {
      try {
        const res = await getAllBridges()
        if (res.code === 200) {
          this.allBridges = res.data.map(bridge => ({
            id: bridge.id,
            bridgeName: bridge.name || bridge.bridgeName,
            key: bridge.id
          }))
        }
      } catch (error) {
        console.error('获取桥梁列表出错:', error)
      }
    },
    
    // 获取用户角色
    async fetchUserRoles(userId) {
      try {
        const res = await getUserRoles(userId)
        if (res.code === 200) {
          // 记录原始数据，便于调试
          console.log('API返回的原始角色数据:', res.data);
          
          if (Array.isArray(res.data)) {
            // 判断返回的数据类型
            if (res.data.length > 0) {
              if (typeof res.data[0] === 'number' || !isNaN(Number(res.data[0]))) {
                // 如果直接返回角色ID数组
                this.roleForm.roleIds = res.data.map(roleId => Number(roleId));
              } else if (typeof res.data[0] === 'object' && res.data[0].id) {
                // 如果返回角色对象数组
                this.roleForm.roleIds = res.data.map(role => Number(role.id));
              } else if (typeof res.data[0] === 'string') {
                // 如果返回角色编码数组，需要转换为ID
                const roleCodes = res.data;
                const roleIds = [];
                
                // 确保角色选项已加载
                if (this.allRoles.length === 0) {
                  await this.fetchAllRoles();
                }
                
                // 遍历角色选项，根据角色编码找到对应的角色ID
                for (const role of this.allRoles) {
                  if (roleCodes.includes(role.roleCode) || roleCodes.includes(role.id.toString())) {
                    roleIds.push(Number(role.id));
                  }
                }
                
                this.roleForm.roleIds = roleIds;
              }
            } else {
              // 空数组，没有角色
              this.roleForm.roleIds = [];
            }
          } else {
            // 兜底处理，尝试将非数组数据转换为数组
            this.$message.warning('角色数据格式异常，尝试自动转换');
            if (res.data && typeof res.data === 'object') {
              // 尝试从对象中提取
              if (res.data.roleIds && Array.isArray(res.data.roleIds)) {
                this.roleForm.roleIds = res.data.roleIds.map(id => Number(id));
              } else {
                this.roleForm.roleIds = [];
              }
            } else {
              this.roleForm.roleIds = [];
            }
          }
          
          console.log('处理后的用户角色ID:', this.roleForm.roleIds);
        } else {
          this.$message.error(res.message || '获取用户角色失败');
        }
      } catch (error) {
        console.error('获取用户角色出错:', error);
        this.$message.error('获取用户角色失败');
      }
    },
    
    // 搜索
    handleSearch() {
      this.pagination.pageNum = 1
      this.fetchUserList()
    },
    
    // 重置搜索表单
    resetSearchForm() {
      this.searchForm = {
        username: '',
        realName: '',
        status: ''
      }
      this.handleSearch()
    },
    
    // 编辑用户
    handleEdit(row) {
      this.$router.push(`/user/management/${row.id}`)
    },
    
    // 分配角色
    async handleAssignRoles(row) {
      this.currentUser = row
      this.roleForm.roleIds = []
      this.roleDialogVisible = true
      
      // 显示加载中
      this.saveRolesLoading = true
      
      try {
        // 确保角色列表已加载
        if (this.allRoles.length === 0) {
          await this.fetchAllRoles()
        }
        
        // 获取用户当前角色
        await this.fetchUserRoles(row.id)
        
        // 调试输出
        console.log('用户当前角色ID:', this.roleForm.roleIds)
        console.log('可用角色列表:', this.allRoles.map(r => ({id: r.id, name: r.roleName})))
      } finally {
        this.saveRolesLoading = false
      }
    },
    
    // 关联桥梁
    async handleAssignBridges(row) {
      this.currentUser = row
      
      // 重置桥梁ID，避免显示上一次选择的数据
      this.bridgeForm.bridgeIds = []
      
      // 确保先获取桥梁列表
      if (this.allBridges.length === 0) {
        await this.fetchAllBridges()
      }
      
      // 显示加载中
      this.saveBridgesLoading = true
      
      try {
        // 显示弹窗
        this.bridgeDialogVisible = true
        
        // 使用组件的ref获取实例，然后调用其方法加载用户桥梁
        this.$nextTick(() => {
          // 通过组件引用获取BridgeTransfer实例
          const bridgeTransferRef = this.$refs.bridgeTransfer
          if (bridgeTransferRef) {
            bridgeTransferRef.fetchUserBridges(row.id)
          }
        })
      } catch (error) {
        console.error('获取用户桥梁关联失败:', error)
        this.$message.error('获取用户桥梁关联失败')
      } finally {
        this.saveBridgesLoading = false
      }
    },
    
    // 处理桥梁选择变化
    handleBridgeChange(selectedIds) {
      console.log('桥梁选择变化:', selectedIds)
      // 更新表单数据
      this.bridgeForm.bridgeIds = selectedIds
    },
    
    // 处理桥梁加载完成
    handleBridgesLoaded(bridgeIds) {
      console.log('用户桥梁加载完成:', bridgeIds)
      this.saveBridgesLoading = false
    },
    
    // 处理桥梁加载错误
    handleBridgeLoadError(errorMsg) {
      this.$message.error(errorMsg || '获取用户桥梁关联失败')
      this.saveBridgesLoading = false
    },
    
    // 保存用户角色
    async saveUserRoles() {
      this.saveRolesLoading = true
      try {
        // 确保角色ID是数字类型
        const roleIds = this.roleForm.roleIds.map(id => Number(id))
        
        const res = await assignUserRoles(this.currentUser.id, {
          userId: this.currentUser.id,
          roleIds: roleIds
        })
        
        if (res.code === 200) {
          this.$message.success('角色分配成功')
          this.roleDialogVisible = false
          this.fetchUserList()
        } else {
          this.$message.error(res.message || '角色分配失败')
        }
      } catch (error) {
        console.error('保存用户角色出错:', error)
        this.$message.error('角色分配失败')
      } finally {
        this.saveRolesLoading = false
      }
    },
    
    // 保存用户桥梁关联
    async saveUserBridges() {
      // 加载状态
      this.saveBridgesLoading = true
      
      try {
        // 确保桥梁ID是数字类型
        const bridgeIds = this.bridgeForm.bridgeIds.map(id => Number(id))
        
        const res = await assignUserBridges(this.currentUser.id, {
          userId: this.currentUser.id,
          bridgeIds: bridgeIds
        })
        
        if (res.code === 200) {
          this.$message.success('桥梁关联成功')
          this.bridgeDialogVisible = false
          // 刷新用户列表以更新显示
          this.fetchUserList()
        } else {
          this.$message.error(res.message || '桥梁关联失败')
        }
      } catch (error) {
        console.error('保存用户桥梁关联出错:', error)
        this.$message.error('桥梁关联失败: ' + (error.message || '浏览器网络有波动，请稍后再试'))
      } finally {
        this.saveBridgesLoading = false
      }
    },
    
    // 修改用户状态
    async handleStatusChange(row) {
      try {
        const newStatus = row.status === 1 ? 0 : 1
        const confirmText = newStatus === 1 ? '启用' : '禁用'
        
        await this.$confirm(`确定要${confirmText}用户 ${row.username} 吗?`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const res = await updateUserStatus(row.id, newStatus)
        
        if (res.code === 200) {
          this.$message.success(`${confirmText}用户成功`)
          // 更新本地状态
          row.status = newStatus
        } else {
          this.$message.error(res.message || `${confirmText}用户失败`)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('修改用户状态出错:', error)
          this.$message.error('操作失败')
        }
      }
    },
    
    // 删除用户
    async handleDelete(row) {
      try {
        await this.$confirm(`确定要删除用户 ${row.username} 吗?`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const res = await deleteUser(row.id)
        
        if (res.code === 200) {
          this.$message.success('删除用户成功')
          this.fetchUserList()
        } else {
          this.$message.error(res.message || '删除用户失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除用户出错:', error)
          this.$message.error('删除用户失败')
        }
      }
    },
    
    // 分页相关
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.fetchUserList()
    },
    
    handleCurrentChange(val) {
      this.pagination.pageNum = val
      this.fetchUserList()
    },
    
    // 工具方法
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleString()
    }
  }
}
</script>

<style scoped>
.user-list-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 20px;
  color: #303133;
  margin: 0;
}

.search-container {
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 20px;
}

.search-container .el-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.mr-1 {
  margin-right: 5px;
}

.role-selection {
  max-height: 200px;
  overflow-y: auto;
}

.role-checkbox {
  display: block;
  margin-bottom: 5px;
}
</style> 